import React, { useState } from 'react';
import { X, ShoppingBag } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import { shop as shopItems } from '../data/site';

const ShopItem = ({ item, onClick }) => (
  <div className="group cursor-pointer" onClick={onClick} data-testid={`shop-item-${item.id}`}>
    <div className="relative overflow-hidden aspect-square bg-[#f7f7f7]">
      <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.03]" />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500 flex items-center justify-center">
        <span className="text-[11px] text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">Quick view</span>
      </div>
      {item.available && (
        <div className="absolute top-3 left-3">
          <span className="text-[10px] bg-white/90 text-[#555] px-2 py-0.5">Available</span>
        </div>
      )}
    </div>
    <div className="mt-3">
      <h3 className="text-[13px] text-[#444] group-hover:text-[#1a1a1a] transition-colors">{item.title}</h3>
      <p className="text-[12px] text-[#aaa] mt-0.5">{item.price}</p>
    </div>
  </div>
);

const QuickViewModal = ({ item, onClose }) => {
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={onClose} data-testid="shop-quick-view">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="aspect-square bg-[#f7f7f7]">
            <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
          </div>
          <div className="p-8 md:p-10 flex flex-col justify-between">
            <div>
              <button className="float-right text-[#ccc] hover:text-[#666] transition-colors" onClick={onClose} data-testid="shop-modal-close"><X size={18} /></button>
              <p className="text-[11px] text-[#bbb] mb-3">{item.category}</p>
              <h2 className="text-lg md:text-xl font-light text-[#1a1a1a] tracking-[0.01em]">{item.title}</h2>
              <p className="text-[16px] text-[#666] mt-3">{item.price}</p>
              {item.available && <p className="text-[11px] text-green-600 mt-2">Available</p>}
              <p className="text-[13px] text-[#999] mt-5 leading-relaxed">
                High-quality archival print of this original artwork by Sebastian McLaughlin. Available in multiple sizes. Each print is produced on premium paper with archival inks for lasting quality.
              </p>
            </div>
            <div className="mt-8">
              <a
                href={`https://sebastianmclaughlinartist.com/shop/p/${item.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2.5 text-[12px] bg-[#1a1a1a] hover:bg-[#333] text-white py-3.5 transition-colors duration-300"
              >
                <ShoppingBag size={13} /> Purchase on original store
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Shop = () => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('All');
  const categories = ['All', 'Print', 'Original'];

  const filteredItems = filter === 'All' ? shopItems : shopItems.filter((it) => it.category === filter);

  return (
    <div data-testid="shop-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-6">
        <FadeIn>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-3xl font-light text-[#1a1a1a] tracking-[0.01em]">Shop</h1>
              <div className="w-10 h-px bg-[#ddd] mt-5" />
            </div>
            <p className="text-[12px] text-[#bbb]">{filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}</p>
          </div>
        </FadeIn>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-8">
        <FadeIn delay={80}>
          <div className="flex gap-5">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`shop-filter-${cat.toLowerCase()}`}
                className={`text-[12px] transition-all duration-300 pb-0.5 ${filter === cat ? 'text-[#1a1a1a] font-medium border-b border-[#1a1a1a]' : 'text-[#bbb] hover:text-[#666]'}`}
              >{cat}</button>
            ))}
          </div>
        </FadeIn>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
          {filteredItems.map((item) => (
            <FadeIn key={item.id}>
              <ShopItem item={item} onClick={() => setSelectedItem(item)} />
            </FadeIn>
          ))}
        </div>
      </section>

      <QuickViewModal item={selectedItem} onClose={() => setSelectedItem(null)} />
    </div>
  );
};

export default Shop;
